import { observer } from 'mobx-react';
import { FC, memo } from 'react';
import OrderTypeCheckboxOption from '../../components/OrderTypeCheckboxOption/OrderTypeCheckboxOption';
import branchEditStore from '../../store/branchEditStore';
import integrationsStore from 'src/mobx/integrationStore';
import { booleanToNumber, isDisabledByPos } from 'src/utils/helpers';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { appStore } from '~/mobx/appStore';
import { localeStore } from '~/mobx/localesStore';
import { informationAlert } from '~/utils/modalConfirmations';
import './styles.scss';
import Switcher from '~/view/components/Controls/Switcher/Switcer';
import Label from '~/view/components/Controls/Label';
import { DELIVERY_TYPE_PICKER_OPTION } from '~/constants/deliveryTypes';
import { TBooleanInNumber } from '~/types/main';
import { modalStore } from '~/mobx/modalStore';
import { pauseDeliveryModalStore } from '~/view/containers/Modals/PauseDeliveryModal/store/pauseDeliveryModalStore';

const BranchOrderTypes: FC = observer((): JSX.Element => {
  const { orderTypes, isEditBranch: isEditScreen, changeOrderTypes, changePauseTime } = branchEditStore;
  const deliveryOrderType = orderTypes.find((type) => type.id === DELIVERY_TYPE_PICKER_OPTION.DELIVERY);
  const isDeliveryPaused = (!!branchEditStore.editableBranch.delivery_paused && !deliveryOrderType?.status) || !!branchEditStore.pauseTime;
  const isValid = branchEditStore.validation.details.orderTypes;
  const branchOrderTypesRestrictionAccess = integrationsStore.posRestrictions?.branchOrderTypes;
  const isBranchOrderTypesDisabledByPos = isDisabledByPos(branchOrderTypesRestrictionAccess);

  const resetPauseTime = () => {
    pauseDeliveryModalStore.updateMinutes('0', false);
    changePauseTime(branchEditStore.editableBranch.delivery_paused ? null : 0);
  };

  const onChangeStatus = (orderTypeId: string | number) => {
    if (appStore.settings?.donation.enabled && branchEditStore.editableBranch.id === appStore.settings?.donation.branch_id) {
      const order_type = orderTypes.find((orderType) => orderType.id === orderTypeId);
      if (order_type?.status && order_type?.type === 'delivery') {
        informationAlert({
          message: localeStore.t('donation_branch_pickup'),
          onConfirm: () => null,
        });
      }
    }
    const updatedOrderTypes = orderTypes.map((orderType) => {
      if (orderType.id !== orderTypeId) return orderType;
      return { ...orderType, status: !orderType.status };
    });

    if (orderTypeId === deliveryOrderType?.id && !deliveryOrderType.status) {
      resetPauseTime();
    }

    changeOrderTypes(updatedOrderTypes);
  };

  const onChangeSwitch = (status: TBooleanInNumber, cb?: () => void) => {
    if (status === 0) {
      resetPauseTime();
    } else {
      modalStore.openPauseDeliveryModal({
        onSave: (minutes: number) => {
          changePauseTime(minutes);
          modalStore.closePauseDeliveryModal();
          if (deliveryOrderType?.id) onChangeStatus(deliveryOrderType.id);
          cb?.();
        },
      });
    }
  };

  return (
    <div className="d-flex flex-column mt-8 mb-4">
      <Text type={TEXT.SECTION_HEADER} text="order_type" className="mb-4" disabled={isBranchOrderTypesDisabledByPos} />
      <div className="d-flex">
        <div className="d-flex flex-column gap-20 order-types-width">
          {orderTypes.map((orderType) => (
            <div className="d-flex">
              <IntegrationManager setting={branchOrderTypesRestrictionAccess} withTooltip>
                <OrderTypeCheckboxOption key={orderType.id} option={orderType} error={!isValid} onClick={onChangeStatus} />
              </IntegrationManager>
              {orderType.id === DELIVERY_TYPE_PICKER_OPTION.DELIVERY && isDeliveryPaused && (
                <Label text={localeStore.t('delivery_paused_label')} className="ml-2" />
              )}
            </div>
          ))}
        </div>
        {isEditScreen && deliveryOrderType?.status && !isDeliveryPaused ? (
          <div className="d-flex align-items-center align-self-start">
            <Switcher hasCallback status={booleanToNumber(isDeliveryPaused)} onChange={onChangeSwitch} />
            <Text type="switcher-label" text="branch_settings_pause_delivery_toggle" />
          </div>
        ) : null}
      </div>
      {!isValid && <Text type={TEXT.ERROR} text="atleast_one_order_type" className="w-fit-content mt-2" />}
    </div>
  );
});

export default memo(BranchOrderTypes);
